import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Save, FileSignature } from 'lucide-react';

const PWN_FIELDS = [
  { key: 'action_proposed', label: 'Description of the Action Proposed or Refused', hint: 'Describe the action the agency proposes or refuses to take.' },
  { key: 'explanation_why', label: 'Explanation of Why the Agency Proposes or Refuses This Action', hint: 'Explain the reasons supporting this decision.' },
  { key: 'options_considered', label: 'Description of Any Options the Agency Considered', hint: 'Describe any other options the agency considered.' },
  { key: 'reasons_rejected', label: 'Reasons the Above Options Were Rejected', hint: 'Explain why the options described above were rejected.' },
  { key: 'evaluation_procedures', label: 'Evaluation Procedures, Tests, Records, or Reports Used as Basis', hint: 'Describe each evaluation procedure, test, record, or report the agency used or will use as a basis for the proposed or refused action.' },
  { key: 'other_factors', label: 'Other Relevant Factors', hint: 'Describe any other factors relevant to the agency\'s proposal or refusal.' },
];

function mergeContent(existing) {
  const defaults = {};
  PWN_FIELDS.forEach(f => { defaults[f.key] = ''; });
  if (!existing || typeof existing !== 'object') return defaults;
  // Migrate from old field names
  if (existing.notes && !existing.action_proposed) return { ...defaults, action_proposed: existing.notes };
  const merged = { ...defaults };
  // Map old field names to new
  if (existing.action_proposed) merged.action_proposed = existing.action_proposed;
  if (existing.explanation_why) merged.explanation_why = existing.explanation_why;
  if (existing.options_considered) merged.options_considered = existing.options_considered;
  if (existing.options_considered_rejected) merged.options_considered = existing.options_considered_rejected;
  if (existing.reasons_rejected) merged.reasons_rejected = existing.reasons_rejected;
  if (existing.evaluation_procedures) merged.evaluation_procedures = existing.evaluation_procedures;
  if (existing.evaluation_procedures_used) merged.evaluation_procedures = existing.evaluation_procedures_used;
  if (existing.other_factors) merged.other_factors = existing.other_factors;
  return merged;
}

export default function METPWNEditor({ section, caseData, onSave, saving }) {
  const sectionKey = section?.section_key || 'met_pwn_eval';
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const handleSave = () => onSave(content);
  const filledCount = PWN_FIELDS.filter(f => (content[f.key] || '').trim()).length;

  const title = sectionKey === 'met_pwn_eligibility' ? 'PWN Eligibility' : 'PWN Evaluation Determination';

  return (
    <div className="max-w-4xl space-y-6" data-testid="met-pwn-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="met-pwn-title">{title}</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <Badge className="bg-[#5A8A5A]/10 text-[#5A8A5A] text-xs mt-2">{filledCount}/{PWN_FIELDS.length} fields completed</Badge>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="met-pwn-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="space-y-4">
        {PWN_FIELDS.map((field, idx) => (
          <div key={field.key} className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={`met-pwn-field-${field.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-[#5A8A5A] bg-[#5A8A5A]/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0">{idx + 1}</span>
              <label className="text-sm font-medium text-[#1C1C1C]">{field.label}</label>
              {(content[field.key] || '').trim() && <span className="ml-auto text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Completed</span>}
            </div>
            <p className="text-xs text-[#595959] mb-3 ml-7">{field.hint}</p>
            <Textarea
              value={content[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
              placeholder={field.hint} rows={4}
              className="rounded-xl border-[#E5E5E5] text-sm"
              data-testid={`met-pwn-input-${field.key}`}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="met-pwn-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : `Save ${title}`}
        </Button>
      </div>
    </div>
  );
}
